import { ReactNode, useMemo } from 'react'
import { InfoOutlined, Clear } from '@mui/icons-material'
import { useDialog } from 'shared/hooks'
import ConfirmDialog from './ConfirmDialog'
import { styled } from '@mui/material/styles'
import { Fab } from '@mui/material'

export enum FlexJustify {
  Start = 'flex-start',
  End = 'flex-end',
}

export enum SingleDetailsAlign {
  Start = 'start',
  End = 'end',
}

const DEFAULT_DETAILS_MARGIN_RIGHT = 24

interface DataGridActionsProps {
  /**
   * Required only when onRemoveClick props is set
   */
  confirmMessage?: string | ReactNode
  onRemoveClick?: () => void
  onShowDetailsClick(): void
  disabledDetails?: boolean
  disabledRemove?: boolean
  detailsMarginRight?: number
  singleDetailsAlign?: SingleDetailsAlign
}

const RemoveFab = styled(Fab)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  border: `${theme.spacing(0.25)} solid`,
  '&:hover': {
    background: theme.palette.grey[200],
  },
}))

const ActionsContainer = styled('div')<{
  justify: FlexJustify
}>(({ justify, theme }) => ({
  display: 'flex',
  width: '100%',
  gap: '16px',
  justifyContent: justify,

  [theme.breakpoints.down('md')]: {
    gap: '8px',
  },
}))

const DataGridActions = ({
  confirmMessage,
  onRemoveClick,
  onShowDetailsClick,
  disabledDetails = false,
  disabledRemove = false,
  detailsMarginRight = DEFAULT_DETAILS_MARGIN_RIGHT,
  singleDetailsAlign = SingleDetailsAlign.Start,
}: DataGridActionsProps) => {
  const [isConfirmDialogOpen, toggleConfirmDialog] = useDialog()

  const onDialogConfirm = async () => {
    if (!onRemoveClick) return
    onRemoveClick()
    toggleConfirmDialog()
  }

  const hasRemoveAction = !!onRemoveClick

  const { justify, detailsInsetMarginRight } = useMemo(() => {
    const nextJustify = hasRemoveAction
      ? FlexJustify.Start
      : singleDetailsAlign === SingleDetailsAlign.Start
      ? FlexJustify.Start
      : FlexJustify.End

    const nextDetailsInsetMarginRight =
      !hasRemoveAction && singleDetailsAlign !== SingleDetailsAlign.Start
        ? detailsMarginRight
        : undefined

    return {
      justify: nextJustify,
      detailsInsetMarginRight: nextDetailsInsetMarginRight,
    }
  }, [detailsMarginRight, hasRemoveAction, singleDetailsAlign])

  return (
    <ActionsContainer justify={justify}>
      <Fab
        color="primary"
        size="small"
        onClick={onShowDetailsClick}
        disabled={disabledDetails}
        style={{
          marginRight: detailsInsetMarginRight,
        }}
      >
        <InfoOutlined />
      </Fab>

      {onRemoveClick && (
        <>
          <RemoveFab
            color="primary"
            size="small"
            onClick={toggleConfirmDialog}
            disabled={disabledRemove}
          >
            <Clear />
          </RemoveFab>

          <ConfirmDialog
            open={isConfirmDialogOpen}
            onConfirm={onDialogConfirm}
            onCancel={toggleConfirmDialog}
            message={confirmMessage || ''}
          />
        </>
      )}
    </ActionsContainer>
  )
}
export default DataGridActions
